import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { ActionWithPayload } from "@obg/ngrx-utils";
import {
	AppError,
	AUTH_ACTION_TYPE,
	CustomerManagementAction,
	Jurisdiction,
	JurisdictionSelector,
	MessageScopeType,
	MessageService,
	MessageType,
	SessionAction,
	SessionConfigService,
} from "@obg/ngx.core";
import { merge, of } from "rxjs";
import { catchError, filter, map, switchMap, withLatestFrom } from "rxjs/operators";

import { MULTI_SESSION_LIMIT_ACTION_TYPE, MultiSessionLimitAction } from "./multi-session-limit.action";
import { MultiSessionLimitConfigService } from "./multi-session-limit.config";
import { MULTI_SESSION_LIMIT_MESSAGE_CODES, MULTI_SESSION_LIMIT_MESSAGE_CONTEXT } from "./multi-session-limit.const";
import { MultiSessionLimit, MultiSessionLimits } from "./multi-session-limit.model";
import { MultiSessionLimitService } from "./multi-session-limit.service";

@Injectable()
export class MultiSessionLimitEffect {
	multiSessionLimits$ = createEffect(() =>
		this.actions$.pipe(
			ofType(MULTI_SESSION_LIMIT_ACTION_TYPE.get, AUTH_ACTION_TYPE.sessionStart),
			filter(() => this.config.getMultiSessionLimitEnabled()),
			switchMap(() =>
				this.service.get().pipe(
					map((response) => this.action.getSuccess(response)),
					catchError((error: AppError) => of(this.action.getFail(error)))
				)
			)
		)
	);

	multiSessionLimitUpdate$ = createEffect(() =>
		this.actions$.pipe(
			ofType(MULTI_SESSION_LIMIT_ACTION_TYPE.update),
			map((x: ActionWithPayload<MultiSessionLimit>) => x.payload),
			switchMap((params) =>
				this.service.update(params).pipe(
					map((response) => this.action.updateSuccess(response)),
					catchError((error: AppError) => of(this.action.updateFail(error)))
				)
			)
		)
	);

	multiSessionLimitUpdateSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(MULTI_SESSION_LIMIT_ACTION_TYPE.updateSuccess),
			map(() => this.customerAction.getCustomer())
		)
	);

	getNextTimeLoginOnUpdateSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(MULTI_SESSION_LIMIT_ACTION_TYPE.updateSuccess),
			map((x: ActionWithPayload<MultiSessionLimits>) => x.payload),
			filter((limits) => !!limits.current && !limits.upcoming && this.sessionConfig.getNextTimeLoginEnabled()),
			map(() => this.sessionAction.getNextTimeLogin())
		)
	);

	multiSessionLimitRemove$ = createEffect(() =>
		this.actions$.pipe(
			ofType(MULTI_SESSION_LIMIT_ACTION_TYPE.remove),
			switchMap(() =>
				this.service.remove().pipe(
					map((response) => this.action.removeSuccess(response)),
					catchError((error: AppError) => of(this.action.removeFail(error)))
				)
			)
		)
	);

	clearOnSessionEnd$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionEnd),
			withLatestFrom(this.store.select(JurisdictionSelector.get)),
			filter(([, jurisdiction]) => jurisdiction !== Jurisdiction.Zhh),
			map(() => this.action.clear())
		)
	);

	initMessages$ = createEffect(() =>
		merge(
			this.messageService.mapErrorByContext$({
				[MULTI_SESSION_LIMIT_ACTION_TYPE.getFail]: MULTI_SESSION_LIMIT_MESSAGE_CONTEXT.get,
				[MULTI_SESSION_LIMIT_ACTION_TYPE.updateFail]: MULTI_SESSION_LIMIT_MESSAGE_CONTEXT.update,
			}),
			this.messageService.mapClearByContext$({
				[MULTI_SESSION_LIMIT_ACTION_TYPE.get]: MULTI_SESSION_LIMIT_MESSAGE_CONTEXT.get,
				[MULTI_SESSION_LIMIT_ACTION_TYPE.update]: MULTI_SESSION_LIMIT_MESSAGE_CONTEXT.update,
			}),
			this.messageService.map$({
				[MULTI_SESSION_LIMIT_ACTION_TYPE.updateSuccess]: {
					context: MULTI_SESSION_LIMIT_MESSAGE_CONTEXT.update,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: MULTI_SESSION_LIMIT_MESSAGE_CODES.updateSuccess,
				},
			}),
			this.messageService.map$({
				[MULTI_SESSION_LIMIT_ACTION_TYPE.removeSuccess]: {
					context: MULTI_SESSION_LIMIT_MESSAGE_CONTEXT.remove,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: MULTI_SESSION_LIMIT_MESSAGE_CODES.removeSuccess,
				},
			})
		)
	);

	constructor(
		private actions$: Actions,
		private store: Store,
		private action: MultiSessionLimitAction,
		private service: MultiSessionLimitService,
		private config: MultiSessionLimitConfigService,
		private customerAction: CustomerManagementAction,
		private messageService: MessageService,
		private sessionAction: SessionAction,
		private sessionConfig: SessionConfigService
	) {}
}
